import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import axios, { API, authHeaders, formatError } from "../../lib/api";

const STATUSES = ["new", "confirmed", "dispatched", "delivered", "cancelled"];

export const OrdersTable = ({ products, onAuthFail }) => {
  const [orders, setOrders] = useState([]);
  const names = Object.fromEntries(products.map((p) => [p.slug, p.name]));

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { headers: authHeaders() });
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else toast.error(formatError(err));
    }
  }, [onAuthFail]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${id}`, { status }, { headers: authHeaders() });
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success(`Order marked ${status}`);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  if (!orders.length) return <p data-testid="admin-orders-empty" className="text-[var(--sage)]">No orders yet.</p>;

  return (
    <div data-testid="admin-orders-list" className="grid gap-3">
      {orders.map((o) => (
        <div key={o.id} data-testid={`admin-order-${o.id}`} className="rounded-[20px] border hairline bg-[var(--forest-2)] p-5 grid gap-4 md:grid-cols-[1.2fr_1fr_1.4fr_170px] items-start">
          <div>
            <p className="font-mono text-xs text-[var(--husk)]">{o.ref}</p>
            <p className="font-semibold text-lg">{o.name}</p>
            <a href={`tel:${o.phone}`} className="inline-flex items-center gap-1.5 text-sm text-[var(--mint)]"><Phone className="h-3.5 w-3.5" />{o.phone}</a>
            {o.company && <p className="text-sm text-[var(--sage)]">{o.company}</p>}
          </div>
          <div>
            <p className="font-display text-2xl font-bold">{o.quantity_t} t</p>
            <p className="text-sm text-[var(--sage)]">{names[o.product_slug] || o.product_slug}</p>
            <p className="font-mono text-[11px] text-[var(--sage)] mt-1">{new Date(o.created_at).toLocaleString("en-IN")}</p>
            <p data-testid={`admin-order-alerts-${o.id}`} className="font-mono text-[11px] text-[var(--sage)] mt-1">Alerts · email {o.alerts?.email ?? "—"} · WhatsApp {o.alerts?.whatsapp ?? "—"}</p>
          </div>
          <div className="text-sm text-[var(--sage)]">
            <p className="text-[var(--cream)]">{o.address}</p>
            {o.notes && <p className="mt-1 italic">{o.notes}</p>}
            {o.email && <p className="mt-1">{o.email}</p>}
          </div>
          <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
            <SelectTrigger data-testid={`admin-order-status-select-${o.id}`} className="h-11 capitalize"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
};
